import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSupabase, hasPersistedSupabaseSession } from '@lib/supabase';
import { api } from '@lib/api';
import { useAuthStore, type AuthMe } from '@store/auth';
import { identifyUser, resetIdentity, track } from '@lib/posthog';

/**
 * Auth state'i Supabase ile senkronize eder.
 * App başlangıcında bir kez çağrılır (App.tsx).
 *
 * Lazy davranış: localStorage'da Supabase token yoksa (anonim ziyaretçi)
 * Supabase modülü hiç indirilmez; loading bayrağı hemen false olur.
 * Login/signup gibi auth aksiyonları gerçekleştiğinde Supabase yüklenir
 * ve onAuthStateChange üzerinden session store'a yansır.
 */
export function useAuthBootstrap() {
  const { setSession, setLoading, clear } = useAuthStore();
  const session = useAuthStore((s) => s.session);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | null = null;

    // Anonim ziyaretçi → Supabase'i hiç yükleme, loading=false ile çık.
    // (Login sonrası ikinci effect subscribe'ı kuracak — bkz. aşağı.)
    if (!hasPersistedSupabaseSession()) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      const supabase = await getSupabase();
      if (!mounted) return;

      // Mevcut session'ı yükle
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);

      // Session değişikliklerini dinle
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, s) => {
        if (!mounted) return;
        setSession(s);
        if (event === 'SIGNED_OUT') {
          clear();
        }
        // Şifre sıfırlama mail linkine tıklandığında Supabase otomatik
        // session oluşturur ve PASSWORD_RECOVERY event'i fırlatır.
        if (event === 'PASSWORD_RECOVERY') {
          if (window.location.pathname !== '/auth/reset-password') {
            window.location.replace('/auth/reset-password');
          }
        }
      });
      unsubscribe = () => subscription.unsubscribe();
    })();

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [setSession, setLoading, clear]);

  // İkinci effect: anonim bootstrap subscribe kurmadıysa, login sonrası
  // session geldiğinde token refresh / logout event'lerini dinlemek için
  // burada subscribe kurulur. (Mobil fresh tarayıcı senaryosu.)
  useEffect(() => {
    if (!session) return;
    let mounted = true;
    let unsubscribe: (() => void) | null = null;
    (async () => {
      const supabase = await getSupabase();
      if (!mounted) return;
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, s) => {
        if (!mounted) return;
        setSession(s);
        if (event === 'SIGNED_OUT') clear();
        if (event === 'PASSWORD_RECOVERY') {
          if (window.location.pathname !== '/auth/reset-password') {
            window.location.replace('/auth/reset-password');
          }
        }
      });
      unsubscribe = () => subscription.unsubscribe();
    })();
    return () => {
      mounted = false;
      unsubscribe?.();
    };
    // session?.user.id değiştiğinde tekrar kurulmasın diye sadece varlık kontrolü
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!session]);
}

/** /auth/me — login sonrası bizim DB'den profil + tenant + izinler çek
 *  Stale-while-revalidate: persist edilmis me varsa UI hemen render eder,
 *  arka planda /auth/me revalidate eder (kullanici 1.16s bos ekran gormez). */
export function useMe() {
  const session = useAuthStore((s) => s.session);
  const setMe = useAuthStore((s) => s.setMe);
  const cachedMe = useAuthStore((s) => s.me);

  return useQuery({
    queryKey: ['auth', 'me', session?.user.id],
    enabled: !!session,
    // localStorage'dan gelen persist me — sayfa yenilemede aninda UI render
    initialData: cachedMe && session && cachedMe.userId === session.user.id ? cachedMe : undefined,
    initialDataUpdatedAt: 0, // hemen background refetch tetiklesin (stale)
    queryFn: async () => {
      let me: AuthMe | null = null;
      try {
        me = await api.get('auth/me').json<AuthMe>();
      } catch {
        // /auth/me 4xx — user henüz DB'de yok, /auth/sync çağırıp tekrar dene
        me = null;
      }

      // tenantId boşsa veya hiç yoksa → /auth/sync çağır
      if (!me || !me.tenantId) {
        const u = session?.user;
        const meta = (u?.user_metadata ?? {}) as Record<string, string>;
        const emailPrefix = u?.email?.split('@')[0] ?? 'Kullanici';
        const firmaAdi =
          (meta.firma_adi || '').trim() || `${emailPrefix} Firmasi`;
        const ad = (meta.ad || '').trim() || emailPrefix;
        const soyad = (meta.soyad || '').trim();
        const telefon = (meta.telefon || '').trim();

        const davetToken = localStorage.getItem('davetToken') ?? undefined;
        await api.post('auth/sync', {
          json: {
            firmaAdi,
            ad,
            ...(soyad && { soyad }),
            ...(telefon && { telefon }),
            kvkkAcikRiza: true,
            ...(davetToken && { davetToken }),
          },
        });
        if (davetToken) localStorage.removeItem('davetToken');
        me = await api.get('auth/me').json<AuthMe>();
      }

      setMe(me);
      identifyUser(me);
      return me;
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Yeni kayıt: Supabase signUp + /auth/sync */
export function useSignUp() {
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: {
      email: string;
      sifre: string;
      firmaAdi: string;
      ad: string;
      soyad: string;
      telefon?: string;
      kvkkAcikRiza: boolean;
    }) => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.signUp({
        email: input.email,
        password: input.sifre,
        options: {
          data: {
            ad: input.ad,
            soyad: input.soyad,
            firma_adi: input.firmaAdi,
          },
          emailRedirectTo: `${window.location.origin}/auth/verify`,
        },
      });
      if (error) throw error;

      if (data.session) {
        const davetToken = localStorage.getItem('davetToken') ?? undefined;
        await fetch(
          `${import.meta.env.VITE_API_URL}/auth/sync`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${data.session.access_token}`,
              'Accept-Language': 'tr',
            },
            body: JSON.stringify({
              firmaAdi: input.firmaAdi,
              ad: input.ad,
              soyad: input.soyad,
              telefon: input.telefon,
              kvkkAcikRiza: input.kvkkAcikRiza,
              ...(davetToken && { davetToken }),
            }),
          },
        );
        if (davetToken) localStorage.removeItem('davetToken');
      }

      // Bkz. useSignIn açıklaması — bootstrap anonim modda subscribe
      // etmediği için session'ı store'a manuel yazıyoruz.
      if (data.session) setSession(data.session);
      return data;
    },
    onSuccess: () => {
      track('signup', { method: 'email' });
      qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

/** Giriş — email + şifre */
export function useSignIn() {
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: { email: string; sifre: string }) => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({
        email: input.email,
        password: input.sifre,
      });
      if (error) throw error;
      // Bootstrap anonim modda erken return ettiyse onAuthStateChange
      // dinleyicisi yok — session'ı store'a kendimiz yazıyoruz ki
      // ProtectedRoute /dashboard'a yönlendirebilsin. (mobilde fresh
      // tarayıcıda login sonrası /auth/login'e geri atma bug'ı).
      if (data.session) setSession(data.session);
      return data;
    },
    onSuccess: () => {
      track('login', { method: 'email' });
      qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

/** Google OAuth */
export function useSignInWithGoogle() {
  return useMutation({
    mutationFn: async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
      return data;
    },
  });
}

/** Şifre sıfırlama linki gönder */
export function useResetPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      track('password_reset_requested');
    },
  });
}

/** Yeni şifre belirle (reset link sonrası) */
export function useUpdatePassword() {
  return useMutation({
    mutationFn: async (yeniSifre: string) => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.updateUser({
        password: yeniSifre,
      });
      if (error) throw error;
      return data;
    },
  });
}

/** Profil bilgilerini güncelle (ad/soyad/telefon) */
export function useUpdateProfil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ad?: string; soyad?: string; telefon?: string }) => {
      return api.patch('auth/profil', { json: input }).json<{
        data: { id: string; ad: string; soyad: string; telefon: string | null };
      }>();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

/** Firma (tenant) bilgilerini güncelle */
export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ad?: string;
      telefon?: string;
      email?: string;
      adres?: string;
      vergiNo?: string;
    }) => {
      return api.patch('auth/tenant', { json: input }).json<{ data: unknown }>();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

/** KVKK açık rıza onayı — eski tenant'ların onaylama akışı */
export function useKvkkOnay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return api.post('auth/kvkk-onay').json<{
        kvkkAcikRiza: boolean;
        idempotent: boolean;
      }>();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

/** Çıkış */
export function useSignOut() {
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const supabase = await getSupabase();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },
    onSuccess: () => {
      track('logout');
      resetIdentity();
      clear();
      qc.clear();
    },
  });
}
